// Swappable catalog data provider (publixx setApiProvider pattern).
//   - 'rest'   : the PIH backend passthrough endpoints (default)
//   - 'static' : raw JSON shipped under public/catalog-data (true offline catalog, no backend)
// Selected via VITE_CATALOG_SOURCE. Phase 2 (Postgres) keeps the same REST contract, so the
// frontend does not change.
//
// This module is deliberately free of any auth code and of any @axa-fr/react-oidc import. It used
// to read the access token itself via `OidcClient.get()`, which is the library's LOW-level entry
// point - and that is exactly what broke: @uag/react-core drives the same library through its HIGH-
// level `<OidcProvider>`, and with two physically separate copies of @axa-fr/react-oidc in the tree
// (7.25.x pulled in by @uag/react-core, 7.27.x direct) the two entry points land in two different
// class instances. The provider then asked a register nobody had ever written to and got "not
// initialized" forever. Deduplicating the copies is not ours to do (DOKA owns the pipeline), so the
// provider no longer opens that second door at all: the token now comes from the official UAG
// module, through the React tree - see CatalogDataProvider.tsx, which builds the provider from
// `useCustomClient` (@uag/react-core) and hands it down via context.

import { apiTestRoutes, type ApiTestRoute } from '../apiTest';
import { isStaticCatalog } from '../authMode';
import { articleFileKey } from './articleFileKey';
import { assetsRoot, recordAssetUrl } from './blobAssets';
import { richTextToPlain } from './richText';

import type { ArticleDetail, AttributeMapping, CatalogConfig, CatalogLabels, DetailLayout, DocumentIndexItem, IndexItem, SystemDetail, Tree } from './types';
import type { AxiosRequestConfig } from 'axios';

export interface CatalogProvider {
    getIndex(lang: string): Promise<IndexItem[]>;
    getTree(): Promise<Tree>;
    getAttributeMapping(): Promise<AttributeMapping>;
    getArticle(artNr: string): Promise<ArticleDetail>;
    /** Detail document for a single System tree node (systems/<nodeId>.json) - the "Systeminformationen" tab. */
    getSystem(nodeId: string): Promise<SystemDetail>;
    getConfig(): Promise<CatalogConfig>;
    getDetailLayout(): Promise<DetailLayout>;
    /** Attribute name / LOV code label table for a language - translates facet values and
     * detail-page attribute values that would otherwise show raw STIBO codes. Empty (no
     * translations known) when the converter run had no data model configured. */
    getLabels(lang: string): Promise<CatalogLabels>;
    /** Catalog-wide document index (documents.json) - powers the document-search mode, fetched
     * lazily (only once the user switches to it, see queries.ts's useDocumentIndex). */
    getDocumentIndex(): Promise<DocumentIndexItem[]>;
    /**
     * Resolves a bare `assetId` (an `IndexItem.image` value or a `MediaItem.assetId`) to a
     * fetchable URL for the **original** asset. The JSON never carries the URL itself - only the
     * reference - so every provider implements this against its own asset location (static
     * `catalog-data/assets/` folder, or the Blob Storage root directly for the REST provider - see
     * assetsBase - never a backend endpoint, in either case).
     */
    getAssetUrl(assetId: string, mimeType?: string): string;
    /** Same as `getAssetUrl`, but for the small preview variant (`assets/tn/`) - used for grid tiles and the detail-page preview. */
    getThumbnailUrl(assetId: string, mimeType?: string): string;
}

/**
 * How a provider fetches one catalog JSON document. The REST provider gets this handed in rather
 * than fetching by itself, because the authenticated variant can only be built inside React (the
 * token lives in the OIDC context, see CatalogDataProvider.tsx). The offline/static provider
 * always uses the plain `fetchJson` below - those files are unprotected assets.
 */
export interface CatalogJsonFetcher {
    <T>(url: string): Promise<T>;
}

/**
 * The request function shape `useCustomClient` (@uag/react-core) returns - the same one orval uses
 * as its mutator (see api/custom-client.tsx). It resolves with the response body, and it is what
 * attaches the bearer token, so this is the only door the catalog uses into the auth layer.
 */
export type CatalogClientRequest = (config: AxiosRequestConfig) => Promise<unknown>;

// The backend response contract has moved: our own passthrough returns the catalog document
// directly, the blueprint API returned { "data": "<document as a JSON string>" }
// (ApiResponseBase<string>) and now returns { "data": <document> } (JsonDocumentResponseBase).
// All three are unwrapped here, once and centrally, so the provider methods below and the types in
// types.ts stay unchanged whichever shape the backend settles on.
const MAX_UNWRAP_STEPS = 3;

// Only an object whose ONLY property is "data" counts as an envelope. Every catalog document has
// several top-level fields and none of them is called "data" (checked against types.ts), so a real
// document can never be mistaken for an envelope and silently unwrapped.
const isDataEnvelope = (value: unknown): value is { data: unknown } =>
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 1 &&
    Object.keys(value)[0] === 'data';

// A full AxiosResponse rather than its body. `useCustomClient` is an orval mutator and therefore
// resolves with the body - but that is a convention, not a type-level guarantee, and a version that
// hands back the whole response would otherwise reach the callers as a document-shaped object with
// no catalog fields at all. Recognised separately from the envelope above (an AxiosResponse has
// several properties, so the single-key check would never match it).
const isAxiosResponseLike = (value: unknown): value is { data: unknown } =>
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    'data' in value &&
    'status' in value &&
    'headers' in value;

// Report each unwrap shape once instead of on every request - this is a contract mismatch worth
// seeing in the console, but not worth spamming while browsing article after article.
const reportedUnwraps = new Set<string>();

const reportUnwrap = (what: string, url: string): void => {
    if (reportedUnwraps.has(what)) {
        return;
    }
    reportedUnwraps.add(what);
    // eslint-disable-next-line no-console
    console.warn(`Catalog response needed unwrapping (${what}), first seen at: ${url}`);
};

const unwrapPayload = (payload: unknown, url: string): unknown => {
    let current = payload;

    for (let step = 0; step < MAX_UNWRAP_STEPS; step++) {
        if (typeof current === 'string') {
            const trimmed = current.trim();
            // A string that doesn't look like JSON is a real (if unexpected) payload - hand it back
            // untouched rather than throwing on it.
            if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
                return current;
            }
            try {
                const parsed: unknown = JSON.parse(trimmed);
                reportUnwrap('JSON string', url);
                current = parsed;
                continue;
            } catch {
                // Looked like JSON but isn't - same reasoning as above, return it as-is.
                return current;
            }
        }

        if (isAxiosResponseLike(current)) {
            reportUnwrap('AxiosResponse instead of its body', url);
            current = current.data;
            continue;
        }

        if (isDataEnvelope(current)) {
            // { "data": <document> } is what the API delivers now (docs/11, point 7) - expected,
            // not worth a warning. Only the old { "data": "<document as a string>" } still reports,
            // through the string branch above on the next step.
            if (typeof current.data !== 'string') {
                current = current.data;
                continue;
            }
            reportUnwrap('{ data: ... } envelope', url);
            current = current.data;
            continue;
        }

        return current;
    }

    return current;
};

/**
 * Shared tail of both fetchers: unwrap whatever envelope the backend used, then reject the two
 * "successful but useless" shapes before they reach a caller that expects a document.
 */
const finishPayload = <T,>(parsed: unknown, url: string): T => {
    const payload = unwrapPayload(parsed, url);

    // A 200 with an empty payload happens when the backend answers an unknown key with
    // { "data": null } instead of a 404 - its OpenAPI declares data as nullable. Callers expect a
    // document and would fail later with a confusing "cannot read properties of null" from deep
    // inside the asset resolution, far from the actual cause.
    if (payload === null || payload === undefined) {
        // eslint-disable-next-line no-console
        console.error(`Catalog response carried no data (empty or "data": null) - ${url}`);
        throw new Error(`Catalog response carried no data: ${url}`);
    }

    // Every catalog endpoint answers with an object or an array (a JSON string carrying one has
    // already been parsed above), so anything still a string here is not catalog data. In practice
    // that is the Static Web App answering an unknown path with index.html (navigationFallback):
    // a perfectly successful 200 full of markup, which without this check would reach the callers
    // as a "document" and fail much later with something unrelated-looking.
    if (typeof payload === 'string') {
        // eslint-disable-next-line no-console
        console.error(
            `Catalog response was text, not a JSON document - ${url}. ` +
                'Check VITE_APP_API_URL: it must point at the backend, not at the frontend.'
        );
        throw new Error(`Catalog response was text, not a JSON document: ${url}`);
    }

    return payload as T;
};

/**
 * Plain, unauthenticated fetch - used by the static/offline provider (its files are unprotected
 * assets under public/) and as the fallback for REST mode with login switched off entirely
 * (VITE_ENABLE_LOGIN=false, see authMode.ts). Protected routes answer 401 through this path, which
 * is the honest outcome: there is no session to take a token from.
 */
export const fetchJson: CatalogJsonFetcher = async <T,>(url: string): Promise<T> => {
    let response: Response;
    try {
        response = await fetch(url, { headers: { Accept: 'application/json' } });
    } catch (cause) {
        // fetch only rejects on network-level failures: refused connection, DNS, or a request
        // blocked by CORS before any response is seen. The browser prints its own note for CORS,
        // but it is easy to miss among framework chatter - and for a refused connection there is
        // nothing at all. Neither case ever reaches the status check below, so without this catch
        // the request fails completely silently.
        // eslint-disable-next-line no-console
        console.error(`Catalog request could not be sent (network error or CORS) - ${url}`, cause);
        throw new Error(`Catalog request could not be sent: ${url}`);
    }

    if (!response.ok) {
        // Log before throwing: every caller runs through react-query, which stores the error in its
        // `error` field without ever printing it. Without this line a failing request is completely
        // silent in the console - the page just stays empty while react-query quietly burns through
        // its three default retries.
        // eslint-disable-next-line no-console
        console.error(`Catalog request failed: ${response.status} ${response.statusText} - ${url}`);
        throw new Error(`Catalog request failed (${response.status}): ${url}`);
    }

    // A 200 carrying HTML means the request reached a static site host, not the API: the Static Web
    // App answers unknown paths with index.html (navigationFallback), so pointing VITE_APP_API_URL
    // at the frontend's own domain produces a perfectly successful response full of markup. Without
    // this check the only symptom is response.json() failing with "Unexpected token '<'", which
    // says nothing about the actual cause.
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('json')) {
        // eslint-disable-next-line no-console
        console.error(
            `Catalog request returned "${contentType}" instead of JSON - ${url}. ` +
                'Check VITE_APP_API_URL: it must point at the backend, not at the frontend.'
        );
        throw new Error(`Catalog request returned ${contentType || 'no content type'} instead of JSON: ${url}`);
    }

    let parsed: unknown;
    try {
        parsed = await response.json();
    } catch (cause) {
        // eslint-disable-next-line no-console
        console.error(`Catalog response was not valid JSON - ${url}`, cause);
        throw new Error(`Catalog response was not valid JSON: ${url}`);
    }

    return finishPayload<T>(parsed, url);
};

/** The status an axios-style rejection carries, if it is one - used for the console diagnostic only. */
const responseStatusOf = (cause: unknown): number | undefined =>
    (cause as { response?: { status?: number } } | undefined)?.response?.status;

/**
 * The authenticated fetcher: every request goes through the request function of
 * `useCustomClient` (@uag/react-core), which attaches the bearer token from the OIDC context and
 * handles renewal. Built from a React component (CatalogDataProvider.tsx) - the hook is what makes
 * the token reachable, and it is the reason this provider is composed inside the React tree instead
 * of at module scope.
 */
export const createClientJson = (request: CatalogClientRequest): CatalogJsonFetcher =>
    async <T,>(url: string): Promise<T> => {
        let parsed: unknown;
        try {
            parsed = await request({ url, method: 'GET', headers: { Accept: 'application/json' } });
        } catch (cause) {
            // Same reasoning as in fetchJson: react-query swallows the error into its `error` field,
            // so without this line a failing catalog call leaves nothing at all in the console. A
            // 401 here means the token was missing or lacks the API scope - check
            // VITE_APP_OIDC_API_SCOPE, which is what puts the backend's audience into the token.
            const status = responseStatusOf(cause);
            // eslint-disable-next-line no-console
            console.error(`Catalog request failed${status ? ` (${status})` : ''} - ${url}`, cause);
            throw new Error(`Catalog request failed${status ? ` (${status})` : ''}: ${url}`);
        }

        return finishPayload<T>(parsed, url);
    };

const restBase = `${import.meta.env.VITE_APP_API_URL ?? ''}/catalog`;
const staticBase = `${import.meta.env.BASE_URL}catalog-data`;

// Root of the Blob Storage container (or whatever fronts it - CDN/Front Door) that the
// pih-stibo-converter publishes assets/ and assets/tn/ under (see CatalogSourceOptions'
// "catalog-out" container, AZURITE-SETUP-JOURNAL.md). Assets and PDFs are pulled straight from
// here, the SAME shape as the static/offline provider below - never through the backend API, even
// in REST mode: unlike the JSON catalog documents, binary assets need no server-side logic
// (filtering/joining/auth is already covered by the JSON), so there is nothing an API hop would
// add except latency, and the published DOKA OpenAPI contract doesn't define asset routes either.
//
// Configured per environment (TEST/DEV/PROD) as domain + container, see blobAssets.ts - which also
// holds the SAS token the private container needs. The URLs built here stay token-FREE on purpose:
// they end up in the react-query cache and in the wishlist (localStorage), and a token baked into
// them would go stale within the hour. Signing happens at render time instead, through
// `useAssetUrl` (blobAssets.ts) - see ProductImage/PdfViewer.
//
// Falls back to the static export's own assets subdirectory (public/catalog-data/assets, same
// staticBase the offline provider uses below) when nothing is configured - an EMPTY base would
// otherwise resolve to a same-origin /assets/... that doesn't exist, 404ing every image/PDF in REST
// mode. The checked-in sample under public/catalog-data thus doubles as the REST provider's default
// asset source too, not just static mode's.
const assetsBase = assetsRoot || staticBase;

// labels.<lang>.json may not exist (no data model configured for that converter run, or the
// static sample predates it) - callers treat an empty table the same as "no translation known".
const emptyLabels = (lang: string): CatalogLabels => ({ language: lang, attributes: {}, lovs: {} });

// Both the static/offline provider and the REST provider's assets resolve this way - plain files
// served with no server-side logic, so (unlike the backend's now-unused assets/{assetId} endpoint,
// which globs the extension) the extension has to be guessed from the mimeType up front.
// '.jpg', not '.jpeg' - confirmed against a real STIBO delivery's actual inbox filenames (the
// ingest pass-through keeps whatever extension STIBO ships, see InboxIngestor.IngestPassThroughAsync);
// '.jpeg' silently 404ed every real product image while only ever matching the (image-less)
// checked-in sample data.
const EXTENSION_BY_MIME_TYPE: Record<string, string> = {
    'application/pdf': '.pdf',
    'image/jpeg': '.jpg',
    'image/png': '.png',
    'image/svg+xml': '.svg',
    'image/webp': '.webp',
};
// Every image call site below always passes an explicit 'image/jpeg' (index items/articles/
// systems carry no mimeType field of their own) - this fallback is only ever actually reached for
// documents/media whose own (optional) mimeType is unset, so it must default to what THOSE are in
// practice (PDF, per the whole PDF-viewer feature built around them), not '.jpg'. Defaulting to
// '.jpg' here previously 404ed any such document under the static/offline export specifically.
const directAssetUrl = (base: string, dir: string) => (assetId: string, mimeType?: string) => {
    const extension = EXTENSION_BY_MIME_TYPE[mimeType ?? ''] ?? '.pdf';
    const url = `${base}/${dir}/${encodeURIComponent(assetId)}${extension}`;

    // Hands the pieces to blobAssets.ts, which keeps the last few of them for pih.lastUrls() and
    // logs them under pih.trace(). The extension in particular is guessed from the mimeType right
    // here (the catalog JSON carries only the bare assetId), and a wrong guess is the likeliest
    // reason for a 404 against a container that is otherwise perfectly reachable.
    recordAssetUrl({ assetId, mimeType, dir, extension, url });

    return url;
};
const staticAssetUrl = (dir: string) => directAssetUrl(staticBase, dir);

/**
 * Resolves index items' `image` assetId to both an original-asset URL (`image`) and a thumbnail
 * URL (`imageThumbnailUrl`) - two sources for the assetId itself, since the delivered index
 * carries no image field of its own: an item's own `image` (if some future index delivery adds
 * one) wins; otherwise the bulk `artNr -> assetId` thumbnail map (fetched once for the whole grid
 * - see `getThumbnails`, avoids one request per tile).
 *
 * Keeping both URLs (not just the thumbnail) matters because `tn/` thumbnails are optional in the
 * Converter pipeline - a delivery may have original product images uploaded but no dedicated
 * thumbnails yet, in which case `assets/tn/{assetId}` 404s. Callers (ProductGrid) use `image` as
 * a fallback source so a tile still shows the real photo instead of nothing.
 */
const resolveIndexImages = (
    items: IndexItem[],
    thumbnails: Record<string, string>,
    assetUrl: CatalogProvider['getAssetUrl'],
    thumbnailUrl: CatalogProvider['getThumbnailUrl']
): IndexItem[] =>
    items.map((item) => {
        const assetId = item.image ?? thumbnails[item.artNr];
        if (!assetId) {
            return item;
        }
        return {
            ...item,
            image: assetUrl(assetId, 'image/jpeg'),
            imageThumbnailUrl: thumbnailUrl(assetId, 'image/jpeg'),
            // Kept as well as the two URLs: it is the id the whole asset chain is debugged by (blob
            // names, pih.find(), a 404 in the network tab), and it is what makes an article findable
            // by its image's UUID - see engine.ts's ID_FIELDS.
            imageAssetId: assetId
        };
    });

/**
 * Merges search-index.<lang>.json's sparse `artNr -> { description, intendedUse }` map into each
 * IndexItem, the same "fetch a bulk map in parallel, merge by artNr" pattern as
 * `resolveIndexImages` (thumbnails) above. Fields absent from an entry are left unmerged - the
 * search index deliberately omits fields with no value rather than padding with "".
 */
type SearchIndexEntry = { description?: string; intendedUse?: string };

const resolveIndexDescriptions = (
    items: IndexItem[],
    descriptions: Record<string, SearchIndexEntry>
): IndexItem[] =>
    items.map((item) => {
        const entry = descriptions[item.artNr];
        if (!entry) {
            return item;
        }
        return {
            ...item,
            // Plain text for the search: STIBO's descriptions can carry (escaped) HTML, and its
            // tags and entities would only match noise ("li", "lt") - see richText.ts.
            ...(entry.description ? { description: richTextToPlain(entry.description) } : {}),
            ...(entry.intendedUse ? { intendedUse: richTextToPlain(entry.intendedUse) } : {}),
        };
    });

/** Resolves each document-index row's assetId to a fetchable URL, unless already set - same
 * shape as `resolveArticleAssets`'s media handling below, just for the catalog-wide list. */
const resolveDocumentAssets = (
    documents: DocumentIndexItem[],
    assetUrl: CatalogProvider['getAssetUrl']
): DocumentIndexItem[] =>
    documents.map((d) => (d.url ? d : { ...d, url: assetUrl(d.assetId, d.mimeType) }));

/**
 * Resolves the article's media assetIds and product image assetId to URLs, unless already set.
 * `media` defaults to `[]` - the static provider's checked-in sample predates the raw STIBO
 * document pipeline and carries no media field. The image resolves to both the original
 * (`image`, click-through target) and a thumbnail (`imageThumbnailUrl`, shown by default).
 */
const resolveArticleAssets = (
    article: ArticleDetail,
    assetUrl: CatalogProvider['getAssetUrl'],
    thumbnailUrl: CatalogProvider['getThumbnailUrl']
): ArticleDetail => ({
    ...article,
    media: (article.media ?? []).map((media) => (media.url ? media : { ...media, url: assetUrl(media.assetId, media.mimeType) })),
    imageThumbnailUrl: article.image ? thumbnailUrl(article.image, 'image/jpeg') : undefined,
    image: article.image ? assetUrl(article.image, 'image/jpeg') : article.image,
});

/**
 * Resolves the system's product-image assetId to both an original-asset URL (`image`) and a
 * thumbnail URL (`imageThumbnailUrl`), and its documents' assetIds to fetchable URLs - the same
 * shape as `resolveArticleAssets`.
 *
 * `media[]` here holds the documents assigned to the system node itself. An earlier version of
 * this comment said systems carried none of their own; that changed when they were moved out of
 * every article's `media[]` (see SystemDetail.media for the measurement behind that).
 */
const resolveSystemAssets = (
    system: SystemDetail,
    assetUrl: CatalogProvider['getAssetUrl'],
    thumbnailUrl: CatalogProvider['getThumbnailUrl']
): SystemDetail => ({
    ...system,
    media: (system.media ?? []).map((media) => (media.url ? media : { ...media, url: assetUrl(media.assetId, media.mimeType) })),
    imageThumbnailUrl: system.image ? thumbnailUrl(system.image, 'image/jpeg') : undefined,
    image: system.image ? assetUrl(system.image, 'image/jpeg') : system.image,
});

/**
 * The backend-backed provider. Takes its fetcher from the caller instead of picking one itself:
 * inside the React tree that is the authenticated `createClientJson(...)`, everywhere else the
 * plain `fetchJson` (see CatalogDataProvider.tsx for which case is which).
 *
 * Asset URLs are plain URL strings by contract (they end up in `<img src>` / the PDF viewer, which
 * cannot carry an Authorization header) - and, per assetsBase's comment above, they go straight at
 * the Blob Storage root rather than through the backend at all, the same shape the static/offline
 * provider below already used.
 */
export const createRestProvider = (getJson: CatalogJsonFetcher): CatalogProvider => {
    const getAssetUrl: CatalogProvider['getAssetUrl'] = directAssetUrl(assetsBase, 'assets');
    const getThumbnailUrl: CatalogProvider['getThumbnailUrl'] = directAssetUrl(assetsBase, 'assets/tn');

    return {
        getIndex: async (lang) => {
            const [items, thumbnails, descriptions] = await Promise.all([
                getJson<IndexItem[]>(`${restBase}/index/${lang}`),
                getJson<Record<string, string>>(`${restBase}/thumbnails`).catch(() => ({}) as Record<string, string>),
                getJson<Record<string, SearchIndexEntry>>(`${restBase}/search-index/${lang}`).catch(
                    () => ({}) as Record<string, SearchIndexEntry>
                ),
            ]);
            return resolveIndexDescriptions(resolveIndexImages(items, thumbnails, getAssetUrl, getThumbnailUrl), descriptions);
        },
        getTree: () => getJson(`${restBase}/tree`),
        getAttributeMapping: () => getJson(`${restBase}/attribute-mapping`),
        getArticle: async (artNr) =>
            resolveArticleAssets(await getJson(`${restBase}/article/${encodeURIComponent(artNr)}`), getAssetUrl, getThumbnailUrl),
        getSystem: async (nodeId) =>
            resolveSystemAssets(await getJson(`${restBase}/system/${encodeURIComponent(nodeId)}`), getAssetUrl, getThumbnailUrl),
        getConfig: () => getJson(`${restBase}/config`),
        getDetailLayout: () => getJson(`${restBase}/detail-layout`),
        getDocumentIndex: async () => resolveDocumentAssets(await getJson(`${restBase}/documents`), getAssetUrl),
        getLabels: (lang) => getJson<CatalogLabels>(`${restBase}/labels/${lang}`).catch(() => emptyLabels(lang)),
        getAssetUrl,
        getThumbnailUrl,
    };
};

const staticProvider: CatalogProvider = {
    // thumbnails.json is a plain run-scoped file the Konverter ships in the static export too
    // (same as tree.json/index/...) - fetch it exactly like the REST provider does, just from the
    // static path. An earlier version of this provider skipped it entirely ("no backend scan"),
    // which meant grid tiles never resolved an image for any real data (only articles/systems
    // with their own `image` field directly on the index row would have shown one, and the real
    // STIBO-derived index never carries that - only thumbnails.json does).
    getIndex: async (lang) => {
        const [items, thumbnails, descriptions] = await Promise.all([
            fetchJson<IndexItem[]>(`${staticBase}/index/index.${lang}.json`),
            fetchJson<Record<string, string>>(`${staticBase}/thumbnails.json`).catch(() => ({}) as Record<string, string>),
            // Same folders as the converter writes and the API reads (search/, labels/ below) - the
            // export is used as-is, no copying files up to the root.
            fetchJson<Record<string, SearchIndexEntry>>(`${staticBase}/search/search-index.${lang}.json`).catch(
                () => ({}) as Record<string, SearchIndexEntry>
            ),
        ]);
        return resolveIndexDescriptions(
            resolveIndexImages(items, thumbnails, staticProvider.getAssetUrl, staticProvider.getThumbnailUrl),
            descriptions
        );
    },
    getTree: () => fetchJson(`${staticBase}/tree.json`),
    getAttributeMapping: () => fetchJson(`${staticBase}/attribute-mapping.json`),
    getArticle: async (artNr) =>
        resolveArticleAssets(
            // The converter's file name, not the raw number - see articleFileKey.ts.
            await fetchJson(`${staticBase}/articles/${encodeURIComponent(articleFileKey(artNr))}.json`),
            staticProvider.getAssetUrl,
            staticProvider.getThumbnailUrl
        ),
    getSystem: async (nodeId) =>
        resolveSystemAssets(
            await fetchJson(`${staticBase}/systems/${encodeURIComponent(nodeId)}.json`),
            staticProvider.getAssetUrl,
            staticProvider.getThumbnailUrl
        ),
    getConfig: () => fetchJson(`${staticBase}/catalog-config.json`),
    getDetailLayout: () => fetchJson(`${staticBase}/detail-layout.json`),
    getDocumentIndex: async () =>
        resolveDocumentAssets(
            await fetchJson<DocumentIndexItem[]>(`${staticBase}/documents.json`).catch(() => [] as DocumentIndexItem[]),
            staticProvider.getAssetUrl
        ),
    getLabels: (lang) => fetchJson<CatalogLabels>(`${staticBase}/labels/labels.${lang}.json`).catch(() => emptyLabels(lang)),
    getAssetUrl: staticAssetUrl('assets'),
    getThumbnailUrl: staticAssetUrl('assets/tn'),
};

// Which REST provider members each ?apitest= route name swaps in. Written as ready-made patch
// objects rather than a name-to-key lookup so the whole table stays type-checked - a typo in a
// method name is a compile error here, not a silently ignored override at runtime.
//
// Note that a route switched to the API is served by the API completely, including the asset URLs
// derived from it: `article` resolves its images through the REST getAssetUrl, so with only
// `?apitest=article` the article data arrives from the backend while its images point at
// assetsBase's Blob Storage root, which may not carry the same runId's assets yet (or may not be
// configured at all locally). Add `asset` to the list, or expect broken images - the point of
// this switch is checking the data, not a mixed-source page.
const apiTestPatches = (restProvider: CatalogProvider): Record<ApiTestRoute, Partial<CatalogProvider>> => ({
    article: { getArticle: restProvider.getArticle },
    system: { getSystem: restProvider.getSystem },
    index: { getIndex: restProvider.getIndex },
    // No standalone CatalogProvider method for either - getIndex already fetches thumbnails and
    // search-index and merges them into each item (see createRestProvider.getIndex above), so
    // exercising those two live routes means going through getIndex just like `index` does.
    thumbnails: { getIndex: restProvider.getIndex },
    'search-index': { getIndex: restProvider.getIndex },
    tree: { getTree: restProvider.getTree },
    config: { getConfig: restProvider.getConfig },
    'detail-layout': { getDetailLayout: restProvider.getDetailLayout },
    'attribute-mapping': { getAttributeMapping: restProvider.getAttributeMapping },
    labels: { getLabels: restProvider.getLabels },
    documents: { getDocumentIndex: restProvider.getDocumentIndex },
    // Original and thumbnail belong together - a page resolving one from the API and the other from
    // the offline bundle would only ever be confusing.
    asset: { getAssetUrl: restProvider.getAssetUrl, getThumbnailUrl: restProvider.getThumbnailUrl }
});

/**
 * Assembles the provider the app actually uses: the source picked by VITE_CATALOG_SOURCE, with the
 * individual routes named in `?apitest=` patched back onto the live API.
 *
 * Called from CatalogDataProvider.tsx (once, inside React) rather than evaluated at module scope,
 * because the REST half needs the authenticated fetcher and that only exists inside the tree.
 */
export const createCatalogProvider = (getJson: CatalogJsonFetcher): CatalogProvider => {
    const restProvider = createRestProvider(getJson);
    const patches = apiTestPatches(restProvider);
    const baseProvider = isStaticCatalog ? staticProvider : restProvider;

    return [...apiTestRoutes].reduce<CatalogProvider>((provider, route) => ({ ...provider, ...patches[route] }), baseProvider);
};
